import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, GridColumn, GridToolbar, } from "@progress/kendo-react-grid";
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { Button } from "@progress/kendo-react-buttons";
import {
  createUser,
  deleteUser as deleteUserAPI,
  getAllUsers,
  searchUsers,
  updateUser,
  importUsers
} from '../../actions/index.js'
import Dialog from '../../components/Dialog/Dialog';
import EditUserForm from '../../components/EditUserForm';
import { process } from '@progress/kendo-data-query';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { Input } from '@progress/kendo-react-inputs';
import { CheckmarkCell, DateCell, EditCommandCell, GroupCell } from '../../components/GridCell'
import { formCloser } from '../../utils/formCloser.js';
import { setToUserDrawer, toggleDrawer } from '../../store/actions/drawerActions.js';
import { esriSuggest } from '../../actions/portal/esri/esriSuggest.js';
import SSOForm from '../../components/SSOForm.jsx';

const initialDataState = {
  skip: 0,
  take: 10,
  // sort: [
  //   { field: 'lastName', dir: 'asc' }
  // ],
}

const DashboardUsers = () => {
  const _export = React.useRef(null);
  const inputFileRef = React.useRef();

  const { token, user } = useSelector(state => state.authReducer);
  const { users, agencies } = useSelector(state => state.usersReducer);
  const { groups } = useSelector(state => state.groupsReducer);
  const { userFilter } = useSelector(state => state.filterReducer);
  const { esriSuggestions } = useSelector(state => state.esriReducer);

  const filteredUsers = users.map(u => {
    let copy = { ...u };
    copy.stringSME = u.sme && u.sme.length ? JSON.parse(u.sme).map(s => s).join(', ') : ""
    copy.stringGroups = u.Groups.map(g => g.name).join(', ');
    copy.excludedGroups = u.uz;
    copy.siteName = u.site.name;
    return copy;
  }).filter(u => {
    if (!userFilter.searchValue
      && userFilter.selectedGroups.length < 1
      && !userFilter.adminFilter
      && userFilter.selectedSiteNames.length < 1
      && !userFilter.enabledFilter) {
      // no filter applied, so return result
      return true;
    }

    let stringMatch = userFilter.searchValue ? false : true;
    let groupMatch = userFilter.selectedGroups && userFilter.selectedGroups.length ? false : true;
    let siteMatch = userFilter.selectedSiteNames && userFilter.selectedSiteNames.length ? false : true;
    let adminFilter = userFilter.adminFilter ? false : true;
    let enabledFilter = userFilter.enabledFilter ? false : true;
    // filter by search value
    if (userFilter.searchValue) {
      ['email', 'firstName', 'lastName', 'agency', 'siteName'].forEach(field => {
        if (u[field] && u[field].toLowerCase().includes(userFilter.searchValue.toLowerCase())) {
          stringMatch = true;
        }
      })
    }
    // filter by groups
    if (userFilter.selectedGroups && userFilter.selectedGroups.length) {
      for (let filterGroup of userFilter.selectedGroups) {
        for (let ug of u.Groups) {
          if (ug.id === filterGroup.id) {
            groupMatch = true;
          }
        }
      }
    }

    if (userFilter.selectedSiteNames && userFilter.selectedSiteNames.length) {
      for (let selectedSiteName of userFilter.selectedSiteNames) {
        if (selectedSiteName.name == u.siteName) {
          siteMatch = true;
        }
      }
    }
    // filter by admin
    if (userFilter.adminFilter) {
      adminFilter = userFilter.adminFilter === u.admin;
    }
    // filter by enabled
    if (userFilter.enabledFilter) {
      enabledFilter = userFilter.enabledFilter === u.enabled;
    }

    return (stringMatch && groupMatch && siteMatch && adminFilter && enabledFilter);
  })

  const [openForm, setOpenForm] = useState(false);
  const [ssoForm, setSsoForm] = useState(false);
  const [dataState, setDataState] = useState(initialDataState);
  const [dialogProps, setDialogProps] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [showSearchClearBtn, setShowSearchClearBtn] = useState(false);

  const dispatch = useDispatch();

  const excelExport = () => {
    if (_export.current !== null) {
      const regex = /([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z)/;
      _export.current.save(filteredUsers.map((user, index) => {
        for (let key of Object.keys(user)) {
          if (regex.test(user[key])) {
            const date = new Date(user[key]);
            user[key] = `${date.getMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`
          }
        }
        return user;
      }), _export.current.columns);
    }
  };

  const addressHandler = (e) => {
    dispatch(esriSuggest(e.value, token))
  }

  const importCSV = (e) => {
    if (e?.target?.files) {
      dispatch(importUsers(e.target.files[0], token))
    }
  }

  const enterEdit = (item) => {
    setOpenForm(true);
    setSelectedUser(item);
  }

  const handleCancelEdit = () => {
    setOpenForm(false);
  };

  const deleteEntry = (dataItem) => {
    // prompt for confirmation
    setDialogProps({
      title: `Delete User`,
      text: `Are you sure you want to delete ${dataItem.firstName} ${dataItem.lastName}?`,
      onConfirm: () => {
        dispatch(deleteUserAPI(dataItem.id, token, formCloser(setShowDialog)))
      },
      enableCancelBtn: true
    });

    setShowDialog(true);
  }

  useEffect(() => {
    if (user.ssoSupport) {
      dispatch(searchUsers(token));
    }
  }, [])


  const editFormSubmit = (event, groupsSelection) => {
    let newSME = event.sme;
    if(Array.isArray(newSME)) {
      newSME = JSON.stringify(newSME);
    }
    if (!event.id) {
      event.groups = groupsSelection;
      event.siteId = user.site.id;
      event.sme = newSME;
      dispatch(createUser(event, token, formCloser(setOpenForm)));
    } else {
      let userData = {
        "email": event.email,
        "firstName": event.firstName,
        "lastName": event.lastName,
        "admin": event.admin,
        "enabled": event.enabled,
        "password": event.password,
        "groups": groupsSelection,
        "phoneNumber": event.phoneNumber,
        "passwordResetReq": event.passwordResetReq,
        "agency": event.agency,
        "sme": newSME,
        "additionalDetails": event.additionalDetails,
        "canOnlySeeContacts": event.canOnlySeeContacts,
        "canOnlySeeGroupContacts": event.canOnlySeeGroupContacts,
        "viewOnly": event.viewOnly,
        "flo": event.flo,
        "cfrComplete": event.cfrComplete,
        "floInfo": event.floInfo,
        "trainingDate": event.trainingDate,
        "county": event.county,
        "azure": event.azure,
        "notificationOnly": event.notificationOnly,
        "agencyAddress": event.agencyAddress,
        "title": event.title,
        "excludedGroups": event.excludedGroups
      }
      dispatch(updateUser(event.id, userData, token, formCloser(setOpenForm)))
    }
  }

  useEffect(() => {
    setDataState(initialDataState);
  }, [userFilter])

  useEffect(() => {
    setInterval(() => dispatch(getAllUsers(token)), 60000)
  }, [])

  return (
    <div>
      <ExcelExport ref={_export} data={filteredUsers}>
        <Grid
          {...dataState}
          resizable={true}
          data={process(filteredUsers, dataState)}
          onDataStateChange={e => setDataState(e.dataState)}
          pageable={true}
          sortable={true}
          style={{ width: '100%' }}
          scrollable='scrollable'
          className='flo-user-grid'
        >
          <GridToolbar>
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                {!user.viewOnly && !user.notificationOnly ? <>
                  <input
                    type="file"
                    ref={inputFileRef}
                    style={{ display: 'none' }}
                    accept=".csv"
                    onChangeCapture={(e) => importCSV(e)}
                  />
                  <Button
                    className="k-button k-primary"
                    style={{
                      minHeight: 40,
                      padding: '10px 20px',
                      marginLeft: 15,
                    }}
                    onClick={() => {
                      inputFileRef.current.click();
                    }}
                    iconClass="k-icon k-i-file-csv"
                  >
                  </Button></> : null}
                {!user.viewOnly && !user.notificationOnly ? <Button
                  className="k-button k-primary"
                  style={{
                    minHeight: 25,
                    padding: '10px 20px',
                    marginLeft: 15,
                  }}
                  onClick={() => {
                    enterEdit({
                      email: "",
                      first_name: "",
                      last_name: "",
                      admin: false,
                      enabled: true,
                      canOnlySeeGroupContacts: true,
                      password: ""
                    })
                  }}
                  iconClass="k-icon k-i-plus"
                >
                  Create User
                </Button> : null}
                <div 
                  style={{
                    paddingLeft: 30,
                  }}
                >
                  <FloatingLabel
                    label='Search'
                    editorId='search-input'
                    editorValue={searchInput}
                  >
                    <Input
                      id='search-input'
                      value={searchInput}
                      onChange={e => {
                        setSearchInput(e.value)
                        setDataState({
                          ...dataState,
                          filter: {
                            logic: 'or',
                            filters: [
                              'email',
                              'firstName',
                              'lastName',
                              'agency',
                              'agencyAddress',
                              'siteName'
                            ].map(field => {
                              return {
                                field,
                                operator: 'contains',
                                value: e.value
                              }
                            })
                          }
                        });

                        setShowSearchClearBtn(e.value ? true : false);
                      }}
                    />
                  </FloatingLabel>
                  {showSearchClearBtn &&
                    <Button
                      className="k-button k-primary"
                      iconClass="k-icon k-i-close"
                      onClick={() => {
                        setDataState(initialDataState);
                        setShowSearchClearBtn(false);
                        setSearchInput('');
                      }}
                    />}
                </div>
              </div>
              <div>
                {user.ssoSupport && <Button
                  className="k-button k-primary"
                  iconClass="k-icon k-i-plus"
                  style={{
                    minHeight: 25,
                    padding: '10px 20px',
                    marginRight: 15,
                  }}
                  onClick={() => setSsoForm(true)}
                >
                Create SSO User
                </Button>}
                <Button
                  className="k-button"
                  style={{
                    minHeight: 25,
                    padding: '10px 20px',
                    marginRight: 15,
                  }}
                  onClick={excelExport}
                >
                  Export to Excel
                </Button>
                <Button
                  className="k-button k-primary"
                  style={{
                    height: 40,
                    width: 40,
                  }}
                  onClick={() => {
                    // ensure user drawer is selected, then show
                    dispatch(setToUserDrawer)
                    dispatch(toggleDrawer)
                  }}
                  iconClass="k-icon k-i-filter"
                />
              </div>
            </div>
          </GridToolbar>
          <GridColumn field="email" width={140} title="Email" />
          <GridColumn field="firstName" width={140} title="First Name" />
          <GridColumn field="lastName" width={140} title="Last Name" />
          <GridColumn field="agency" width={140} title="Agency" />
          <GridColumn field="agencyAddress" width={140} title="Agency Address"/>
          <GridColumn
            field="stringGroups"
            title="Groups"
            width={140}
            cell={props => <GroupCell groups={props.dataItem.Groups} />}
          />
          <GridColumn 
            field="stringSME"
            title="SME"
            width={140}
          />
          <GridColumn 
            field="county"
            title="County"
            width={140}
          />
          <GridColumn
            title="Admin"
            field="admin"
            cell={props => <CheckmarkCell checked={props.dataItem.admin} />}
            width={100}
          />
          <GridColumn
            title="Enabled"
            field="enabled"
            filter="boolean"
            cell={props => <CheckmarkCell checked={props.dataItem.enabled} />}
            width={100}
          />
          {user?.site?.sitePermissions?.some(perm => perm.permissionName === 'FLO_FIELDS') &&
            <GridColumn 
              title="FLO"
              field="flo"
              filter="boolean"
              cell={props => <CheckmarkCell checked={props.dataItem.flo} />}
              width={100}
            />}
          {user?.site?.sitePermissions?.some(perm => perm.permissionName === 'FLO_FIELDS') &&
            <GridColumn 
              title="CFR"
              field="cfrComplete"
              filter="boolean"
              cell={props => <CheckmarkCell checked={props.dataItem.cfrComplete} />}
              width={100}
            />}
          {user?.site?.sitePermissions?.some(perm => perm.permissionName === 'FLO_FIELDS') &&
            <GridColumn 
              title="FLO Info"
              field="floInfo"
              filter="boolean"
              cell={props => <CheckmarkCell checked={props.dataItem.floInfo} />}
              width={100}
            />}
          {user?.site?.sitePermissions?.some(perm => perm.permissionName === 'FLO_FIELDS') &&  
            <GridColumn
              title="Training"
              field="trainingDate"
              cell={props => <DateCell date={props.dataItem.trainingDate} />}
              width={100} 
            />}
          <GridColumn
            title="SSO"
            field="azure"
            filter="boolean"
            cell={props => <CheckmarkCell checked={props.dataItem.azure} />}
            width={100}
          />
          <GridColumn 
            field="phoneNumber"
            title="Cell"
            width={140}
          />
          <GridColumn 
            field="title"
            title="Title"
            width={140}
          />
          <GridColumn
            title="Created"
            field="createdAt"
            cell={props => <DateCell date={props.dataItem.createdAt} />}
            width={140}
          />
          <GridColumn
            title="Updated"
            field="updatedAt"
            cell={props => <DateCell date={props.dataItem.updatedAt} />}
            width={140}
          />
          <GridColumn
            title="Last Login"
            field="lastLogin"
            cell={props => <DateCell date={props.dataItem.lastLogin} />}
            width={140}
          />
          <GridColumn
            locked={true}
            cell={(props) => (
              <EditCommandCell
                {...props}
                locked={true}
                user={user}
                enterEdit={enterEdit}
                deleteEntry={(dataItem) => deleteEntry(dataItem)}
              />
            )}
            width={100}
          />
        </Grid>
      </ExcelExport>
      {openForm && (
        <EditUserForm
          groups={groups}
          agencies={agencies}
          addressHandler={addressHandler}
          addressDataSource={esriSuggestions}
          cancelEdit={handleCancelEdit}
          onSubmit={editFormSubmit}
          item={selectedUser}
          currentUser={user}
        />
      )}
      {ssoForm && (
        <SSOForm 
        groups={groups}
        agencies={agencies}
        cancelEdit={() => setSsoForm(false)}
        onSubmit={(p) => {
          setSelectedUser(p);
          setSsoForm(false)
          setOpenForm(true);
          console.log(p)
        }}
        item={selectedUser}
        currentUser={user}
        />
      )

      }
      {showDialog &&
        <Dialog
          {...dialogProps}
          onClose={() => setShowDialog(false)}
        />}
    </div>
  )
}

export default DashboardUsers;